import React, { FC, useEffect } from 'react';
import { useState } from 'react';
import { observer } from 'mobx-react';
import { KeyboardEvents } from '../../../constants/common.constants';
import './tags-input.scss';

const COMMA_SEPARATOR = ',';
const SPACE_SEPARATOR = ' ';
const SEMICOLON_SEPARATOR = ';';
interface TagsInputProps {
    tagsList: string[];
    selectedTags: string[];
    setSelectedTags: (tags: string[]) => void;
    onBlur?: () => void;
}

interface TagProps {
    tag: string;
    removeTag: (tag: string) => void;
}

const Tag: FC<TagProps> = ({ tag, removeTag }) => {
    return (<span className='tag'>
        {tag}
        <span className='remove-tag' onClick={() => removeTag(tag)}>×</span>
    </span>
    );
};

const TagsInput: FC<TagsInputProps> = observer(({
    tagsList,
    selectedTags = [],
    setSelectedTags,
    onBlur,
}) => {
    const [input, setInput] = useState<string>('');
    const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
    const [filteredTags, setFilteredTags] = useState<string[]>(tagsList);

    useEffect(() => {
        setFilteredTags(tagsList.filter(tag =>
            !selectedTags.includes(tag) &&
            tag.toLowerCase().includes(input.toLowerCase())
        ));
    }, [input, selectedTags]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        setInput(value);
        const shouldCreateNewTag = value.includes(COMMA_SEPARATOR) || value.trimStart().includes(SPACE_SEPARATOR)
            || value.includes(SEMICOLON_SEPARATOR);

        if (shouldCreateNewTag) {
            const [newTag, ...remaining] = value.split(COMMA_SEPARATOR);
            if (newTag.trim()) {
                selectTag(newTag.trim().replace(SEMICOLON_SEPARATOR, ''));
            }
            setInput(remaining.join(COMMA_SEPARATOR).trimStart());
        }

        if (!dropdownVisible) setDropdownVisible(true);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === KeyboardEvents.ENTER && input.trim()) {
            event.preventDefault();
            selectTag(input.trim());
            setInput('');
        }
        if (event.key === KeyboardEvents.BACKSPACE) {
            if (!event.currentTarget.value.length && selectedTags.length) {
                setSelectedTags(selectedTags.slice(0, selectedTags.length - 1));
            }
        }
    };

    const handleFocus = (): void => {
        if (filteredTags.length > 0) {
            setDropdownVisible(true);
        }
    };

    const handleBlur = (): void => {
        if (onBlur) onBlur();
        setTimeout(() => setDropdownVisible(false), 300);
    };

    const selectTag = (tag: string): void => {
        if (!selectedTags.includes(tag)) {
            setSelectedTags([...selectedTags, tag]);
            setInput('');
            setDropdownVisible(false);
        }
    };

    const removeTag = (tagToRemove: string): void => {
        setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
    };

    return (
        <div className='tag-input'>
            <div
                title={selectedTags.join('\n')}
                className='tags-container'
            >
                {selectedTags && selectedTags
                    .filter(Boolean)
                    .map((tag: string) => <Tag key={tag} tag={tag} removeTag={removeTag} />)
                }
                <input
                    type='text'
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
            </div>
            {dropdownVisible && filteredTags.length > 0 && (
                <ul className='filtered-tags'>
                    {filteredTags.map((tag: string) => (
                        <li key={tag} onClick={() => selectTag(tag)}>
                            {tag}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
});


export { TagsInput, Tag };